import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import bindAllActions from '../../common/bindAllActions'
import { hasHst, getDeviceLabel } from '../../common/ambient'
import { ambient } from '@aw_products/ambient-weather-common'
import { useUpdateSetting, useUpdateGroupMembers } from '../user/redux/hooks'
import { isEmailVerified, isPhoneVerified } from './AlertSetMemberCard.helpers'
import { getHeatStressTier, getHeatStressTierLimits } from '../payment'
import Avatar from '../user/Avatar'
import './HeatStressAlertDevices.less'

const HST = ambient.HST

function HeatStressAlertDevices ({ device }) {
  const devices = path(['devices'], device) || []
  const hstDevices = devices.filter(hasHst)
  const { userChecked, info } = useUpdateSetting()
  const { updateGroupMembers } = useUpdateGroupMembers('heatStressGroupMembers')
  const user = useSelector(state => state.user)
  const [openMenus, setOpenMenus] = useState({}) // Track which device dropdowns are open

  if (hstDevices.length === 0) {
    return null
  }

  // Get members eligible for email alerts (verified email, including admin)
  const getEligibleMembers = () => {
    const members = info?.heatStressGroupMembers || []
    return members.filter(m => m.email && isEmailVerified(m, user))
  }

  // Get options for react-select (include email and phone for filtering)
  const getMemberOptions = (eligibleMembers) => {
    return eligibleMembers.map(m => ({
      label: m.name || m.email,
      value: m._id,
      email: m.email,
      phone: m.phone
    }))
  }

  // Get selected member IDs from member.devicesEmail arrays
  const getSelectedMemberIds = (macAddress) => {
    const members = info?.heatStressGroupMembers || []
    return members
      .filter(m => m.devicesEmail && m.devicesEmail.includes(macAddress))
      .map(m => m._id)
  }

  // Get selected options for react-select (reads from members)
  const getSelectedOptions = (macAddress, memberOptions) => {
    const selectedIds = getSelectedMemberIds(macAddress)
    return memberOptions.filter(opt => selectedIds.includes(opt.value))
  }

  // Handle recipient change - UPDATE MEMBERS
  const handleRecipientChange = (macAddress, selected) => {
    const newSelectedIds = selected ? selected.map(s => s.value) : []
    const allMembers = info?.heatStressGroupMembers || []

    // Update each member's devicesEmail array
    const updatedMembers = allMembers.map(member => {
      const currentDevices = member.devicesEmail || []
      const isNowSelected = newSelectedIds.includes(member._id)
      const wasSelected = currentDevices.includes(macAddress)

      // No change needed
      if (isNowSelected === wasSelected) {
        return member
      }

      // Add device to member's devicesEmail
      if (isNowSelected && !wasSelected) {
        return {
          ...member,
          devicesEmail: [...currentDevices, macAddress]
        }
      }

      // Remove device from member's devicesEmail
      if (!isNowSelected && wasSelected) {
        return {
          ...member,
          devicesEmail: currentDevices.filter(mac => mac !== macAddress)
        }
      }

      return member
    })

    // Save to server via updateGroupMembers (debounced 500ms)
    updateGroupMembers(updatedMembers)
  }

  // Toggle all/none - UPDATE MEMBERS
  const handleToggleAll = (e, macAddress, currentSelected, allOptions) => {
    e.preventDefault()
    e.stopPropagation()

    const allIds = allOptions.map(opt => opt.value)
    const shouldSelectAll = currentSelected.length !== allIds.length

    handleRecipientChange(macAddress, shouldSelectAll ? allOptions : [])
  }

  // Track menu open/close state
  const handleMenuOpen = (macAddress) => {
    setOpenMenus(prev => ({ ...prev, [macAddress]: true }))
  }

  const handleMenuClose = (macAddress) => {
    setOpenMenus(prev => ({ ...prev, [macAddress]: false }))
  }

  // Check if menu is open for a device
  const isMenuOpen = (macAddress) => {
    return openMenus[macAddress] || false
  }

  // Custom filter function to search name, email, and phone
  const filterOption = (option, inputValue) => {
    if (!inputValue) return true

    const searchValue = inputValue.toLowerCase()
    const { label, email, phone } = option.data

    return (
      (label && label.toLowerCase().includes(searchValue)) ||
      (email && email.toLowerCase().includes(searchValue)) ||
      (phone && phone.toLowerCase().includes(searchValue))
    )
  }

  const eligibleMembers = getEligibleMembers()
  const memberOptions = getMemberOptions(eligibleMembers)

  // Determine if SMS alerts are available for this user
  const isSmsEnabled = () => {
    const currentTier = getHeatStressTier({ info })
    const tierLimits = getHeatStressTierLimits(currentTier)
    return tierLimits.smsEnabled
  }

  // Get tier display name (null for free tier)
  const getTierDisplayName = () => {
    const currentTier = getHeatStressTier({ info })
    if (currentTier === 'free') return null
    const tierLimits = getHeatStressTierLimits(currentTier)
    return tierLimits.displayName
  }

  // Get members eligible for SMS alerts (verified phone, including admin)
  const getEligibleSmsMembers = () => {
    const members = info?.heatStressGroupMembers || []
    return members.filter(m => m.phone && isPhoneVerified(m, user))
  }

  // Get SMS member options for react-select
  const getSmsMemberOptions = (eligibleSmsMembers) => {
    return eligibleSmsMembers.map(m => ({
      label: m.name || m.phone,
      value: m._id,
      email: m.email,
      phone: m.phone
    }))
  }

  // Get selected SMS member IDs from member.devicesSms arrays
  const getSelectedSmsMemberIds = (macAddress) => {
    const members = info?.heatStressGroupMembers || []
    return members
      .filter(m => m.devicesSms && m.devicesSms.includes(macAddress))
      .map(m => m._id)
  }

  // Get selected SMS options for react-select (reads from members)
  const getSelectedSmsOptions = (macAddress, smsMemberOptions) => {
    const selectedIds = getSelectedSmsMemberIds(macAddress)
    return smsMemberOptions.filter(opt => selectedIds.includes(opt.value))
  }

  // Handle SMS recipient change - UPDATE MEMBERS
  const handleSmsRecipientChange = (macAddress, selected) => {
    const newSelectedIds = selected ? selected.map(s => s.value) : []
    const allMembers = info?.heatStressGroupMembers || []

    // Update each member's devicesSms array
    const updatedMembers = allMembers.map(member => {
      const currentDevices = member.devicesSms || []
      const isNowSelected = newSelectedIds.includes(member._id)
      const wasSelected = currentDevices.includes(macAddress)

      // No change needed
      if (isNowSelected === wasSelected) {
        return member
      }

      // Add device to member's devicesSms
      if (isNowSelected && !wasSelected) {
        return {
          ...member,
          devicesSms: [...currentDevices, macAddress]
        }
      }

      // Remove device from member's devicesSms
      if (!isNowSelected && wasSelected) {
        return {
          ...member,
          devicesSms: currentDevices.filter(mac => mac !== macAddress)
        }
      }

      return member
    })

    // Save to server via updateGroupMembers (debounced 500ms)
    updateGroupMembers(updatedMembers)
  }

  // Get selected SMS members for Avatar display
  const getSelectedSmsMembers = (macAddress) => {
    const selectedIds = getSelectedSmsMemberIds(macAddress)
    const eligibleSmsMembers = getEligibleSmsMembers()
    return eligibleSmsMembers.filter(m => selectedIds.includes(m._id))
  }

  // Custom option renderer with checkbox
  const formatOptionLabel = (option, { context, selectValue }) => {
    // Manually check if this option is in the selected values
    const isSelected = selectValue && selectValue.some(selected => selected.value === option.value)
    return (
      <div className='custom-option'>
        <div className={classNames('checkbox-indicator', { checked: isSelected })} />
        <span className='option-name'>{option.label}</span>
      </div>
    )
  }

  // Get selected member objects for Avatar display
  const getSelectedMembers = (macAddress) => {
    const selectedIds = getSelectedMemberIds(macAddress)
    return eligibleMembers.filter(m => selectedIds.includes(m._id))
  }

  // Helper to get active zones for a device
  const getActiveZones = (dev) => {
    const hstSettings = path(['settings', 'hst'], dev)
    if (!hstSettings || !hstSettings.guideline) return []

    // Get resolved guideline with modifiers applied
    const guideline = HST.getFinalGuildeline(hstSettings)
    if (!guideline || !guideline.zones) return []

    const enabledThresholds = hstSettings.enabledThresholds || []

    // Filter zones to only those with enabled alerts
    return guideline.zones.filter(zone =>
      enabledThresholds.includes(zone.above)
    )
  }

  // Helper to get zone color
  const getZoneColor = (zoneLabel) => {
    const colorObj = HST.colors.find(c => c.zoneLabel === zoneLabel)
    return colorObj ? '#' + colorObj.bkgndColor : null
  }

  // Helper to get guideline name
  const getGuidelineName = (dev) => {
    const hstSettings = path(['settings', 'hst'], dev)
    if (!hstSettings || !hstSettings.guideline) return null

    return hstSettings.guideline === 'custom'
      ? 'Custom'
      : HST.guidelines[hstSettings.guideline]?.label || 'Unknown'
  }

  return (
    <div>
      <h3>Heat Stress Alert Devices</h3>
      <div className='subscriptions-grid'>
        {hstDevices.map((dev) => {
          const deviceLabel = getDeviceLabel(dev)
          const macAddress = dev.macAddress
          const deviceLocation = dev?.info?.coords?.location || 'Location not set'
          const activeZones = getActiveZones(dev)

          return (
            <div key={macAddress} className='device-subscription-card'>
              <div className='card-header'>
                {deviceLabel}
              </div>
              <div className='card-body'>
                <div className='device-meta'>
                  <div className='meta-row mac-address'>
                    <span className='label'>MAC:</span>
                    <span className='value'>{macAddress}</span>
                  </div>
                  <div className='meta-row location'>
                    <span className='label'>Location:</span>
                    <span className='value'>{deviceLocation}</span>
                  </div>
                </div>

                {activeZones.length > 0 && (
                  <div className='active-zones'>
                    <div className='zones-label'>Active Heat Stress Zones</div>
                    <div className='guideline-name'>Guideline: {getGuidelineName(dev)}</div>
                    <div className='zones-list'>
                      {activeZones.map((zone, idx) => {
                        const color = getZoneColor(zone.label)
                        return (
                          <div key={idx} className='zone-item'>
                            <div
                              className='zone-color'
                              style={color ? { backgroundColor: color } : {}}
                            />
                            <span className='zone-name'>{zone.label}</span>
                          </div>
                        )
                      })}
                    </div>
                    <a href={`/devices#mac-${macAddress}`}>
                      Manage zones
                    </a>
                  </div>
                )}

                {/* Email Recipients Section */}
                {userChecked && (
                  <div className='email-recipients-section'>
                    <div className='recipients-header'>
                      <span className='recipients-label email-label'>Email Alerts</span>
                    </div>

                    {eligibleMembers.length > 0 ? (
                      <>
                        <Select
                          autoComplete='off'
                          className='email-recipients-select'
                          classNamePrefix='email-select'
                          isMulti={true}
                          options={memberOptions}
                          value={getSelectedOptions(macAddress, memberOptions)}
                          onChange={(selected) => handleRecipientChange(macAddress, selected)}
                          placeholder='Manage Email recipients'
                          formatOptionLabel={formatOptionLabel}
                          hideSelectedOptions={false}
                          closeMenuOnSelect={false}
                          controlShouldRenderValue={false}
                          isClearable={false}
                          filterOption={filterOption}
                          onMenuOpen={() => handleMenuOpen(macAddress)}
                          onMenuClose={() => handleMenuClose(macAddress)}
                        />

                        {/* Selected Members as Avatars */}
                        {getSelectedMembers(macAddress).length > 0 ? (
                          <div className='selected-members-list'>
                            {getSelectedMembers(macAddress).map(member => (
                              <div key={member._id} className='selected-member-item'>
                                <Avatar name={member.admin ? null : member.name || member.email} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className='no-recipients-message'>No email recipients</div>
                        )}
                      </>
                    ) : (
                      <div className='no-members-state'>
                        <Select
                          autoComplete='off'
                          className='email-recipients-select'
                          classNamePrefix='email-select'
                          isDisabled={true}
                          placeholder='No verified email members'
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* SMS Recipients Section */}
                {userChecked && isSmsEnabled() && (() => {
                  const eligibleSmsMembers = getEligibleSmsMembers()
                  const smsMemberOptions = getSmsMemberOptions(eligibleSmsMembers)
                  const tierName = getTierDisplayName()

                  return (
                    <div className='sms-recipients-section'>
                      <div className='recipients-header'>
                        <span className='recipients-label sms-label'>SMS Alerts</span>
                        {tierName && (
                          <span className='tier-display'>
                            <span className='tier-name'>{tierName}</span>
                          </span>
                        )}
                      </div>

                      <Select
                        autoComplete='off'
                        className='sms-recipients-select'
                        classNamePrefix='sms-select'
                        isMulti={true}
                        options={smsMemberOptions}
                        value={getSelectedSmsOptions(macAddress, smsMemberOptions)}
                        onChange={(selected) => handleSmsRecipientChange(macAddress, selected)}
                        placeholder={eligibleSmsMembers.length > 0 ? 'Manage SMS recipients' : 'No verified SMS members'}
                        formatOptionLabel={formatOptionLabel}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={false}
                        controlShouldRenderValue={false}
                        isClearable={false}
                        filterOption={filterOption}
                        onMenuOpen={() => handleMenuOpen(macAddress + '-sms')}
                        onMenuClose={() => handleMenuClose(macAddress + '-sms')}
                        isDisabled={eligibleSmsMembers.length === 0}
                      />

                      {/* Selected SMS Members as Avatars */}
                      {getSelectedSmsMembers(macAddress).length > 0 ? (
                        <div className='selected-members-list'>
                          {getSelectedSmsMembers(macAddress).map(member => (
                            <div key={member._id} className='selected-member-item'>
                              <Avatar name={member.name || member.phone} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='no-recipients-message'>No SMS recipients</div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

HeatStressAlertDevices.propTypes = {
  device: PropTypes.object.isRequired
}

HeatStressAlertDevices.displayName = 'HeatStressAlertDevices'

export default bindAllActions(HeatStressAlertDevices)
